import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, CalendarClock, CheckCircle2, ChevronDown, ChevronUp, Loader2,
  Mail, MessageSquare, Phone, RefreshCw, Repeat, Send, X,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { devDispatchAppointmentConfirmationEmails } from "@/lib/apiClient";
import {
  CLINIC_TIME_ZONE,
  clinicDateAt,
  clinicDateStringFromInstant,
  clinicTimePartsFromInstant,
} from "@/lib/clinicTime";
import { MIN_SLOT_INTERVAL_MINUTES, normalizeSlotIntervalMinutes } from "@/lib/calendarGranularity";

const RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "this_week", label: "This week" },
];

const TABS = [
  { key: "call", label: "Call list" },
  { key: "reschedule", label: "Reschedule queue" },
  { key: "confirmed", label: "Confirmed" },
  { key: "emails", label: "Emails sent" },
];

const EMAIL_TYPES = [
  { value: "all", label: "All types" },
  { value: "confirmation_request", label: "Confirmation request" },
  { value: "reschedule_confirmed", label: "Reschedule confirmed" },
  { value: "reschedule_declined", label: "Reschedule declined" },
];

const DELIVERY_STATUSES = [
  { value: "all", label: "All statuses" },
  { value: "sent", label: "Sent" },
  { value: "opened", label: "Opened" },
  { value: "delivered", label: "Delivered" },
  { value: "bounced", label: "Bounced" },
];

const ATTEMPT_METHODS = [
  { value: "call", label: "Call" },
  { value: "sms", label: "SMS" },
  { value: "messenger", label: "Messenger" },
];

const ATTEMPT_OUTCOMES = [
  { value: "no_answer", label: "No answer" },
  { value: "left_voicemail", label: "Left voicemail" },
  { value: "confirmed_verbally", label: "Confirmed verbally" },
  { value: "will_call_back", label: "Will call back" },
];

function isLocalCalendarDevHost() {
  if (typeof window === "undefined") return false;
  return ["localhost", "127.0.0.1", "::1"].includes(window.location?.hostname);
}

export default function AppointmentWorklistPanel({ doctors = [], chairs = [], branchId = null, onChanged }) {
  const [range, setRange] = useState("tomorrow");
  const [activeTab, setActiveTab] = useState("call");
  const [emailKind, setEmailKind] = useState("all");
  const [deliveryStatus, setDeliveryStatus] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [devDispatching, setDevDispatching] = useState(false);
  const [attemptDrafts, setAttemptDrafts] = useState({});
  const [previewOpen, setPreviewOpen] = useState({});
  const [modalRow, setModalRow] = useState(null);

  const load = useCallback(async () => {
    if (!branchId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data: payload, error } = await supabase.rpc("list_appointment_worklist", {
      p_range: range,
      p_email_kind: emailKind,
      p_delivery_status: deliveryStatus,
      p_branch_id: branchId,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message || "Could not load appointment worklist.");
      return;
    }
    setData(payload || {});
  }, [range, emailKind, deliveryStatus, branchId]);

  useEffect(() => { load(); }, [load]);

  const metrics = data?.metrics || {};
  const rows = {
    call: data?.call_list || [],
    reschedule: data?.reschedule_queue || [],
    confirmed: data?.confirmed || [],
    emails: data?.emails || [],
  };
  const showDevDispatch = isLocalCalendarDevHost();

  async function afterMutation(message) {
    await load();
    onChanged?.();
    if (message) toast.success(message);
  }

  async function markConfirmed(row) {
    setBusyKey(`${row.appointment_id}:confirm`);
    const { error } = await supabase.rpc("mark_appointment_confirmed_by_staff", {
      p_appointment_id: row.appointment_id,
    });
    setBusyKey(null);
    if (error) {
      toast.error(error.message || "Could not mark confirmed.");
      return;
    }
    afterMutation("Appointment confirmed.");
  }

  async function devSendDueEmails() {
    setDevDispatching(true);
    const overrideAppointmentIds = rows.call
      .map((row) => row.appointment_id)
      .filter(Boolean)
      .slice(0, 5);
    const result = await devDispatchAppointmentConfirmationEmails({
      limit: 5,
      overrideAppointmentIds,
    });
    setDevDispatching(false);
    if (!result?.ok) {
      toast.error(result?.message || "Could not send due confirmation emails.");
      return;
    }
    await afterMutation(`Email dispatch checked: ${result.sent || 0} sent, ${result.failed || 0} failed.`);
  }

  async function logAttempt(row) {
    const draft = attemptDrafts[row.appointment_id] || {};
    const method = draft.method || "call";
    const outcome = draft.outcome || "no_answer";
    setBusyKey(`${row.appointment_id}:attempt`);
    const { error } = await supabase.rpc("log_appointment_contact_attempt", {
      p_appointment_id: row.appointment_id,
      p_method: method,
      p_outcome: outcome,
      p_note: null,
    });
    setBusyKey(null);
    if (error) {
      toast.error(error.message || "Could not log attempt.");
      return;
    }
    afterMutation("Attempt logged.");
  }

  function setAttempt(row, patch) {
    setAttemptDrafts((current) => ({
      ...current,
      [row.appointment_id]: { method: "call", outcome: "no_answer", ...current[row.appointment_id], ...patch },
    }));
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="appointment-worklist-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-3 py-3">
        <div>
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-teal-600" />
            <h2 className="text-sm font-bold text-slate-950">Appointment worklist</h2>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">Confirmation calls, reschedules, confirmed patients, and appointment email history.</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {showDevDispatch ? (
            <button
              type="button"
              onClick={devSendDueEmails}
              disabled={devDispatching || loading}
              data-testid="worklist-dev-send-due-emails"
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-bold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
              title="Temporary localhost-only test control"
            >
              {devDispatching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send shown emails now
            </button>
          ) : null}
          <select
            value={range}
            onChange={(event) => setRange(event.target.value)}
            data-testid="worklist-range"
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700"
          >
            {RANGE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <button type="button" onClick={load} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Refresh appointment worklist">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div className="grid gap-2 border-b border-slate-100 p-3 sm:grid-cols-3">
        <MetricCard label="Need to call" value={metrics.need_to_call || 0} tone="rose" />
        <MetricCard label="Reschedule queue" value={metrics.reschedule_queue || 0} tone="amber" />
        <MetricCard label="Confirmed" value={metrics.confirmed || 0} tone="emerald" />
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-slate-100 px-3">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            data-testid={`worklist-tab-${tab.key}`}
            className={[
              "inline-flex items-center gap-2 border-b-2 px-3 py-2 text-xs font-bold",
              activeTab === tab.key
                ? "border-slate-900 text-slate-950"
                : "border-transparent text-slate-500 hover:text-slate-800",
            ].join(" ")}
          >
            {tab.label}
            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
              {tab.key === "call" ? rows.call.length : tab.key === "reschedule" ? rows.reschedule.length : tab.key === "confirmed" ? rows.confirmed.length : rows.emails.length}
            </span>
          </button>
        ))}
      </div>

      <div className="p-3">
        {loading ? (
          <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-4 text-xs text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading appointment worklist...
          </div>
        ) : activeTab === "call" ? (
          <CallList rows={rows.call} busyKey={busyKey} attemptDrafts={attemptDrafts} onAttemptChange={setAttempt} onLogAttempt={logAttempt} onReschedule={setModalRow} onMarkConfirmed={markConfirmed} />
        ) : activeTab === "reschedule" ? (
          <RescheduleQueue rows={rows.reschedule} clinicPhone={data?.clinic?.phone} onPickSlot={setModalRow} />
        ) : activeTab === "confirmed" ? (
          <ConfirmedList rows={rows.confirmed} />
        ) : (
          <EmailsSent
            rows={rows.emails}
            summary={data?.email_summary || {}}
            trackingActive={!!data?.delivery_tracking_active}
            emailKind={emailKind}
            deliveryStatus={deliveryStatus}
            onEmailKind={setEmailKind}
            onDeliveryStatus={setDeliveryStatus}
            previewOpen={previewOpen}
            onTogglePreview={(id) => setPreviewOpen((current) => ({ ...current, [id]: !current[id] }))}
          />
        )}
      </div>

      {modalRow ? (
        <RescheduleModal
          row={modalRow}
          doctors={doctors}
          chairs={chairs}
          onClose={() => setModalRow(null)}
          onDone={() => { setModalRow(null); afterMutation("Appointment rescheduled."); }}
        />
      ) : null}
    </section>
  );
}

function MetricCard({ label, value, tone }) {
  const tones = {
    rose: "text-rose-700 bg-rose-50",
    amber: "text-amber-700 bg-amber-50",
    emerald: "text-emerald-700 bg-emerald-50",
  };
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 inline-flex rounded-md px-2 py-0.5 text-xl font-black tabular-nums ${tones[tone]}`}>{value}</p>
    </div>
  );
}

function CallList({ rows, busyKey, attemptDrafts, onAttemptChange, onLogAttempt, onReschedule, onMarkConfirmed }) {
  if (rows.length === 0) return <EmptyState text="No unconfirmed appointments in this range." />;
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Scheduled and not confirmed</p>
      {rows.map((row) => {
        const busy = busyKey?.startsWith(row.appointment_id);
        const draft = attemptDrafts[row.appointment_id] || { method: "call", outcome: "no_answer" };
        return (
          <article key={row.appointment_id} className="rounded-lg border border-slate-200 p-3">
            <div className="flex flex-wrap items-start gap-3">
              <Avatar name={row.patient_name} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-slate-950">{row.patient_name || "Patient"}</p>
                <p className="mt-0.5 text-xs text-slate-500">{formatSlot(row)} · {row.doctor_name || "Doctor"} · {row.chair_name || "Chair"}</p>
                <AttemptDots count={row.contact_attempt_count || 0} method={row.last_attempt_method} at={row.last_attempt_at} />
              </div>
              <div className="flex flex-wrap justify-end gap-1.5">
                <a href={row.patient_phone ? `tel:${row.patient_phone}` : undefined} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                  <Phone className="h-3.5 w-3.5" /> Call
                </a>
                <a href={row.patient_phone ? `sms:${row.patient_phone}` : undefined} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                  <MessageSquare className="h-3.5 w-3.5" /> SMS
                </a>
                <button type="button" onClick={() => onReschedule(row)} disabled={busy} data-testid={`worklist-reschedule-${row.appointment_id}`}
                  className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-200 disabled:opacity-50">
                  <Repeat className="h-3.5 w-3.5" /> Reschedule
                </button>
                <button type="button" onClick={() => onMarkConfirmed(row)} disabled={busy}
                  className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Mark confirmed
                </button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-2">
              <select value={draft.method} onChange={(event) => onAttemptChange(row, { method: event.target.value })} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs">
                {ATTEMPT_METHODS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select value={draft.outcome} onChange={(event) => onAttemptChange(row, { outcome: event.target.value })} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs">
                {ATTEMPT_OUTCOMES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <button type="button" onClick={() => onLogAttempt(row)} disabled={busy} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 disabled:opacity-50">
                Log attempt
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function RescheduleQueue({ rows, clinicPhone, onPickSlot }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Patient-requested reschedules</p>
      {rows.length === 0 ? <EmptyState text="No reschedule requests in this range." /> : rows.map((row) => (
        <article key={row.request_id || row.appointment_id} className="rounded-lg border border-slate-200 p-3">
          <div className="flex flex-wrap items-start gap-3">
            <Avatar name={row.patient_name} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-slate-950">{row.patient_name || "Patient"}</p>
              <p className="mt-0.5 text-xs text-slate-500">Original: {formatSlot(row)} · {row.doctor_name || "Doctor"}</p>
              <span className="mt-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-amber-800">Reschedule request</span>
            </div>
            <button type="button" onClick={() => onPickSlot(row)} className="inline-flex items-center gap-1 rounded-md bg-teal-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-teal-700">
              <Repeat className="h-3.5 w-3.5" /> Pick new slot
            </button>
          </div>
          {row.patient_message ? <p className="mt-3 rounded-md border-l-2 border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950">"{row.patient_message}"</p> : null}
        </article>
      ))}
      <div className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 text-slate-400" />
        <span>To cancel your appointment, please call us at {clinicPhone || "the clinic"}.</span>
      </div>
    </div>
  );
}

function ConfirmedList({ rows }) {
  if (rows.length === 0) return <EmptyState text="No confirmed appointments in this range." />;
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Confirmed appointments</p>
      {rows.map((row) => (
        <article key={row.appointment_id} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3">
          <Avatar name={row.patient_name} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-slate-950">{row.patient_name || "Patient"}</p>
            <p className="mt-0.5 text-xs text-slate-500">{formatSlot(row)} · {row.doctor_name || "Doctor"}</p>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-800">
            <CheckCircle2 className="h-3.5 w-3.5" /> Confirmed
          </span>
        </article>
      ))}
    </div>
  );
}

function EmailsSent({ rows, summary, trackingActive, emailKind, deliveryStatus, onEmailKind, onDeliveryStatus, previewOpen, onTogglePreview }) {
  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-4">
        <MetricCard label="Total sent" value={summary.total_sent || 0} tone="emerald" />
        <MetricCard label="Opened" value={summary.opened || 0} tone="emerald" />
        <MetricCard label="Delivered" value={summary.delivered || 0} tone="amber" />
        <MetricCard label="Bounced" value={summary.bounced || 0} tone="rose" />
      </div>
      {!trackingActive ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Delivery tracking is not active yet. Rows show Sent in v1.
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <select value={emailKind} onChange={(event) => onEmailKind(event.target.value)} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs">
          {EMAIL_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <select value={deliveryStatus} onChange={(event) => onDeliveryStatus(event.target.value)} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs">
          {DELIVERY_STATUSES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>
      {rows.length === 0 ? <EmptyState text="No appointment emails in this range." /> : rows.map((row) => {
        const bounced = row.delivery_status === "bounced";
        return (
          <article key={row.dispatch_id} className={`rounded-lg border p-3 ${bounced ? "border-rose-300" : "border-slate-200"}`}>
            <div className="flex flex-wrap items-start gap-3">
              <Avatar name={row.patient_name} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-slate-950">{row.patient_name || "Patient"}</p>
                <p className="mt-0.5 text-xs text-slate-500">{row.patient_email || "No email"} · {formatDateTime(row.appointment_start)} appt</p>
              </div>
              <div className="flex flex-wrap justify-end gap-1.5">
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">{emailKindLabel(row.email_kind)}</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-800">
                  <Send className="h-3 w-3" /> {titleCase(row.delivery_status || "sent")}
                </span>
                <span className="text-xs text-slate-400">{formatTime(row.sent_at)}</span>
              </div>
            </div>
            {bounced ? <p className="mt-2 text-xs font-semibold text-rose-700">Email address invalid. Contact this patient manually — add to call list.</p> : null}
            <button type="button" onClick={() => onTogglePreview(row.dispatch_id)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
              {previewOpen[row.dispatch_id] ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              Preview email
            </button>
            {previewOpen[row.dispatch_id] ? (
              <div className="mt-2 rounded-md border-l-2 border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
                <p className="font-semibold text-slate-800">Subject: {row.subject || "No subject recorded"}</p>
                <p className="mt-1 whitespace-pre-wrap">{row.body || "No body recorded."}</p>
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

function RescheduleModal({ row, doctors, chairs, onClose, onDone }) {
  const initial = useMemo(() => {
    const date = clinicDateStringFromInstant(row.scheduled_start);
    const parts = clinicTimePartsFromInstant(row.scheduled_start);
    return { date, time: `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}` };
  }, [row.scheduled_start]);
  const durationMin = useMemo(() => minutesBetween(row.scheduled_start, row.scheduled_end), [row.scheduled_start, row.scheduled_end]);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [doctorId, setDoctorId] = useState(row.doctor_id || doctors[0]?.id || "");
  const [chairId, setChairId] = useState(row.chair_id || chairs[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    let requestId = row.request_id || null;
    if (!requestId) {
      const { data, error: requestError } = await supabase.rpc("create_staff_reschedule_request", {
        p_appointment_id: row.appointment_id,
        p_patient_message: null,
      });
      if (requestError) {
        setBusy(false);
        setError(requestError.message || "Could not create reschedule request.");
        return;
      }
      requestId = data?.request_id || null;
    }

    const [hour = "0", minute = "0"] = time.split(":");
    const nextStart = clinicDateAt(date, Number(hour), Number(minute));
    const nextEnd = new Date(nextStart.getTime() + durationMin * 60_000);
    const { error: moveError } = await supabase.rpc("reschedule_appointment_in_place", {
      p_appointment_id: row.appointment_id,
      p_new_start: nextStart.toISOString(),
      p_new_end: nextEnd.toISOString(),
      p_new_chair_id: chairId || null,
      p_new_doctor_id: doctorId || null,
      p_request_id: requestId,
      p_patient_message: row.patient_message || null,
    });
    setBusy(false);
    if (moveError) {
      setError(moveError.message || "Could not reschedule appointment.");
      return;
    }
    onDone?.();
  }

  return (
    <div className="fixed inset-0 z-[2147483640] flex items-center justify-center bg-slate-950/45 px-4 py-6" data-testid="worklist-reschedule-modal">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div>
            <h3 className="text-base font-bold text-slate-950">Reschedule appointment</h3>
            <p className="mt-0.5 text-xs text-slate-500">{row.patient_name || "Patient"} · {formatSlot(row)}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close reschedule modal">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 p-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-[11px] font-black uppercase tracking-wider text-slate-500">Line items — carried over</p>
            <div className="mt-2 divide-y divide-slate-200">
              {(row.line_items || []).length === 0 ? (
                <p className="py-2 text-xs text-slate-500">No attached procedures or billing records found.</p>
              ) : row.line_items.map((item, index) => (
                <div key={`${item.type || "item"}-${index}`} className="flex items-start gap-2 py-2 text-xs">
                  {item.type === "billing" ? <Mail className="mt-0.5 h-3.5 w-3.5 text-slate-400" /> : <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 text-teal-600" />}
                  <div>
                    <p className="font-semibold text-slate-800">{item.label || item.procedure_name || "Line item"}</p>
                    {item.visit_reference ? <p className="text-slate-500">{item.visit_reference}</p> : null}
                    {item.amount_centavos || item.fee_centavos ? <p className="text-slate-500">{formatPHP(item.amount_centavos || item.fee_centavos)}</p> : null}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs font-medium text-slate-500">All procedures and billing records move with this appointment. Nothing is re-entered.</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="New date">
              <input type="date" value={date} onChange={(event) => setDate(event.target.value)} className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm" />
            </Field>
            <Field label="New time">
              <input type="time" value={time} onChange={(event) => setTime(event.target.value)} className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm" />
            </Field>
            <Field label="Doctor">
              <select value={doctorId} onChange={(event) => setDoctorId(event.target.value)} className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm">
                <option value="">Unassigned</option>
                {doctors.map((doctor) => <option key={doctor.id} value={doctor.id}>{doctor.display_name || doctor.name || "Doctor"}</option>)}
              </select>
            </Field>
            <Field label="Chair">
              <select value={chairId} onChange={(event) => setChairId(event.target.value)} className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm">
                <option value="">No chair</option>
                {chairs.map((chair) => <option key={chair.id} value={chair.id}>{chair.name}</option>)}
              </select>
            </Field>
          </div>

          {error ? <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div> : null}

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
            <button type="button" onClick={onClose} disabled={busy} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
            <button type="button" onClick={confirm} disabled={busy} data-testid="worklist-reschedule-confirm" className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-teal-700 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Confirm reschedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function Avatar({ name }) {
  const initials = String(name || "P")
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-50 text-xs font-black text-teal-700">{initials || "P"}</span>;
}

function AttemptDots({ count, method, at }) {
  const filled = Math.min(Number(count || 0), 3);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {[0, 1, 2].map((idx) => <span key={idx} className={`h-1.5 w-1.5 rounded-full ${idx < filled ? "bg-emerald-500" : "bg-slate-200"}`} />)}
      <span className="text-[11px] text-slate-500">
        {filled > 0 ? `${filled} of 3 attempts · last: ${titleCase(method || "attempt")} ${formatTime(at)}` : "No attempts yet"}
      </span>
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center text-xs text-slate-500">{text}</div>;
}

function formatSlot(row) {
  return `${formatDateTime(row.scheduled_start)} – ${formatTime(row.scheduled_end)}`;
}

function formatDateTime(value) {
  if (!value) return "No time";
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: CLINIC_TIME_ZONE,
  });
}

function formatTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: CLINIC_TIME_ZONE,
  });
}

function minutesBetween(start, end, fallback = MIN_SLOT_INTERVAL_MINUTES) {
  const a = new Date(start);
  const b = new Date(end);
  const fallbackMin = normalizeSlotIntervalMinutes(fallback);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return fallbackMin;
  return Math.max(fallbackMin, Math.round((b.getTime() - a.getTime()) / 60_000));
}

function formatPHP(centavos) {
  const amount = Number(centavos || 0) / 100;
  return amount.toLocaleString(undefined, { style: "currency", currency: "PHP" });
}

function emailKindLabel(kind) {
  if (kind === "initial" || kind === "reminder") return "Confirmation request";
  if (kind === "reschedule_approved") return "Reschedule confirmed";
  if (kind === "reschedule_declined") return "Reschedule declined";
  return titleCase(kind || "Email");
}

function titleCase(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
